import {
	Box,
	Icons,
	Table,
	AsyncSelect,
	Dropdown,
	Stack,
	Text,
	Inline,
	joinAttributes,
	Time,
	Badge,
} from "@sembark-travel/ui-base"
import {
	NavLink,
	queryToSearch,
	useLocationQuery,
	ButtonLink,
	Link,
} from "@sembark-travel/ui-router"
import {
	ListView,
	TSearchParams,
	useSearch,
	Search,
} from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import { Omit } from "utility-types"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import {
	DateRangePickerField,
	isTruthy,
	SelectField,
	SwitchInputField,
} from "@sembark-travel/ui-form"
import config from "../config"
import { generatePath } from "../router-utils"
import { EditItemInDialog } from "./EditItem"
import { AddTripSourceDialog } from "./NewItem"
import { ITripSource, ITripSourceTag, tripSourcesXHR } from "./store"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { ILocation, SelectLocations } from "../Locations"
import { TemporaryStorage } from "../DownloadManager"
import { SelectTripSourceTags } from "./Tag"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import { SelectTeams, TTeam } from "../Teams"
import { IUser, SelectUsers } from "../Users"
import { TagBadges } from "../Tags"

type TFilters = TSearchParams & {
	stats_start?: Date
	stats_end?: Date
	created_after?: Date
	created_before?: Date
	locations?: Array<ILocation>
	tags?: Array<ITripSourceTag>
	teams?: Array<TTeam>
	created_by?: Array<IUser>
	disabled_only?: 1 | 0
}

type TFiltersInQuery = TSearchParams & {
	ss?: string
	se?: string
	ca?: string
	cb?: string
	locations?: Array<string>
	tags?: Array<string>
	teams?: Array<string>
	created_by?: Array<string>
	disabled_only?: 1 | 0
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		q,
		page,
		stats_start,
		stats_end,
		created_after,
		created_before,
		locations,
		tags,
		teams,
		created_by,
		disabled_only,
	} = filters
	const query: TFiltersInQuery = {}
	if (page) {
		query.page = page
	}
	if (q) {
		query.q = q
	}
	if (stats_start) {
		query.ss = dateToQuery(stats_start)
	}
	if (stats_end) {
		query.se = dateToQuery(stats_end)
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (locations?.length) {
		query.locations = locations.map((l) => `${l.id}__${l.full_name}`)
	}
	if (tags?.length) {
		query.tags = tags.map((t) => `${t.id}__${t.name}`)
	}
	if (teams?.length) {
		query.teams = teams.map((t) => `${t.id}__${t.name}`)
	}
	if (created_by?.length) {
		query.created_by = created_by.map((c) => `${c.id}__${c.name}`)
	}
	if (disabled_only) {
		query.disabled_only = 1
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		q,
		page,
		ss,
		se,
		ca,
		cb,
		locations,
		tags,
		teams,
		created_by,
		disabled_only,
	} = query
	const filters: TFilters = {}
	if (page) {
		filters.page = page
	}
	if (q) {
		filters.q = q
	}
	if (ss) {
		filters.stats_start = parseDateFromQuery(ss)
	}
	if (se) {
		filters.stats_end = parseDateFromQuery(se)
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (locations?.length) {
		filters.locations = locations.map((l) => {
			const [id, ...name] = l.split("__")
			return {
				id,
				full_name: name.join("__"),
			} as never as ILocation
		})
	}
	if (tags?.length) {
		filters.tags = tags.map((t) => {
			const [id, ...name] = t.split("__")
			return { id: id, name: name.join("__") } as never as ITripSourceTag
		})
	}
	if (teams?.length) {
		filters.teams = teams.map((t) => {
			const [id, ...name] = t.split("__")
			return { id, name: name.join("__") } as never as TTeam
		})
	}
	if (created_by) {
		filters.created_by = created_by.map((c) => {
			const [id, ...name] = c.split("__")
			return {
				id,
				name: name.join("__"),
			} as never as IUser
		})
	}
	if (disabled_only) {
		filters.disabled_only = 1
	}
	return filters
}

function filtersToRequest({
	stats_start,
	stats_end,
	created_after,
	created_before,
	locations,
	tags,
	disabled_only,
	teams,
	created_by,
	...params
}: TFilters) {
	return {
		...params,
		stats_start: stats_start
			? dateToUTCString(startOf(stats_start, "day"))
			: null,
		stats_end: stats_end ? dateToUTCString(endOf(stats_end, "day")) : null,
		created_after: created_after
			? dateToUTCString(startOf(created_after, "day"))
			: null,
		created_before: created_before
			? dateToUTCString(endOf(created_before, "day"))
			: null,
		locations: locations?.length ? locations.map((l) => l.id) : [],
		tags: tags?.length ? tags.map((t) => t.id) : [],
		teams: teams?.length ? teams.map((t) => t.id) : [],
		created_by: created_by?.length ? created_by.map((c) => c.id) : [],
		disabled_only: isTruthy(disabled_only) ? 1 : undefined,
	}
}

export function List({ title = "B2B Sources" }: { title?: string }) {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	const { hasPermission } = useCheckPermissions()
	const { user } = useAuthUser()
	const canViewAllSources =
		hasPermission(PERMISSIONS.ADD_TRIP_DESTINATIONS) ||
		isTruthy(user?.show_only_assigned_trip_sources)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const disabledOnly = isTruthy(params.disabled_only)
	return (
		<Search
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			title={title}
			Filters={canViewAllSources ? Filters : undefined}
			actions={
				hasPermission(PERMISSIONS.ADD_TRIP_SOURCES) ? (
					<Inline gap="4">
						<ButtonLink
							to={generatePath("/trip-sources/new")}
							level="primary"
							status="primary"
							anchored
						>
							<Icons.Plus title="Add" /> New B2B Source
						</ButtonLink>
						<Dropdown alignRight="sm">
							<Dropdown.ToggleButton>
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
									<Dropdown.MenuItem
										as={Link}
										to={generatePath("/trip-sources/tags")}
									>
										B2B Source Tags
									</Dropdown.MenuItem>
								) : null}
								<Dropdown.MenuItem
									as={Link}
									to={generatePath("/trip-sources/merge")}
								>
									Merge Duplicate B2B Sources
								</Dropdown.MenuItem>
								{canViewAllSources ? (
									<TemporaryStorage
										initUrl="/trip-sources/download"
										initParams={{
											...filtersToRequest(params),
											timezone_offset: config.timezoneOffset,
										}}
									>
										{({ message, generate, url, reset }) =>
											url ? (
												<Dropdown.MenuItem
													as="a"
													href={url}
													target="_blank"
													download
													onClick={() => reset()}
												>
													{message}
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														generate()
													}}
												>
													{message || (
														<>
															<Icons.DocumentDownload /> Download as CSV/Excel
														</>
													)}
												</Dropdown.MenuItem>
											)
										}
									</TemporaryStorage>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				) : null
			}
		>
			<ListView<ITripSource, TFilters>
				pageKey="trip-sources"
				fetch={(xhr, params) =>
					tripSourcesXHR(xhr).getTripSources({
						...filtersToRequest(params),
						include: "connected_tenant,address,created_by,tags,teams",
						sort: "-created_at",
						page: canViewAllSources ? params.page : 1,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
				params={params}
				paginationDisabled={!canViewAllSources}
			>
				{({ items: tripSources, refresh }) => (
					<Table
						striped
						bordered
						headers={["Name", "Created By", "Tags", "Teams", ""]}
						responsive
						alignCols={{ 4: "right" }}
						rows={tripSources.map((tripSource) => [
							<Link
								to={generatePath("/trip-sources/:tripSourceId", {
									tripSourceId: tripSource.id.toString(),
								})}
								color="accent"
								fontWeight="semibold"
								anchored
							>
								<Inline gap="6" alignItems={"start"}>
									<Stack gap="px">
										<Text>
											{tripSource.name} ({tripSource.short_name})
										</Text>
										<Inline fontSize="sm">
											{joinAttributes(
												tripSource.address
													? tripSource.address.location.name
													: null,
												tripSource.is_agent ? "B2B Agent" : null
											)}
										</Inline>
									</Stack>
									{disabledOnly ? (
										<Badge status="warning">Disabled</Badge>
									) : null}
								</Inline>
							</Link>,
							<Stack gap="px">
								<Text>{tripSource.created_by?.name || "You"}</Text>
								<Text color="muted" fontSize="sm">
									<Time timestamp={tripSource.created_at} />
								</Text>
							</Stack>,
							<TagBadges tags={tripSource.tags} />,
							<Inline color={"muted"}>
								{(tripSource.teams || []).length > 0
									? (tripSource.teams || [])
											.map((team) => team.name)
											.join(" • ")
									: "-"}
							</Inline>,
							<Box>
								{hasPermission(PERMISSIONS.ADD_TRIP_SOURCES) ? (
									<Dropdown alignRight>
										<Dropdown.ToggleButton level="tertiary" size="sm">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											{disabledOnly ? null : (
												<EditItemInDialog
													tripSource={tripSource}
													onSuccess={() => {
														refresh()
													}}
												>
													{({ edit }) => (
														<Dropdown.MenuItem onClick={() => edit()}>
															<Icons.Pencil /> Edit Details
														</Dropdown.MenuItem>
													)}
												</EditItemInDialog>
											)}
											<NavLink
												to={
													generatePath("/trips") +
													queryToSearch({
														sources: [`${tripSource.id}_${tripSource.name}`],
														status: "all",
													})
												}
											>
												<Icons.Preview /> View Queries
											</NavLink>
											{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
												<DisableEnableTripSource
													tripSource={tripSource}
													isDisabled={disabledOnly}
													onSuccess={() => {
														refresh()
													}}
												>
													{({ onToggleStatus, isProcessing }) => (
														<Dropdown.MenuItem
															onClick={onToggleStatus}
															color={disabledOnly ? "success" : "danger"}
														>
															{isProcessing ? (
																<>
																	<Icons.Refresh spin /> Processing...
																</>
															) : disabledOnly ? (
																<>
																	<Icons.Refresh /> Enable
																</>
															) : (
																<>
																	<Icons.Ban /> Disable
																</>
															)}
														</Dropdown.MenuItem>
													)}
												</DisableEnableTripSource>
											) : null}
										</Dropdown.Menu>
									</Dropdown>
								) : null}
							</Box>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

export function DisableEnableTripSource({
	tripSource,
	onSuccess,
	children,
	isDisabled,
}: {
	tripSource: ITripSource
	onSuccess?: () => void
	children: (props: {
		onToggleStatus: () => void
		isProcessing: boolean
	}) => React.ReactNode
	isDisabled: boolean
}) {
	const xhr = useXHR()
	const [isProcessing, setIsProcessing] = useState(false)

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					{children({
						isProcessing,
						onToggleStatus: async () => {
							if (isProcessing) return
							if (await confirm()) {
								setIsProcessing(true)
								try {
									const endpoint = `/trip-sources/${tripSource.id}${isDisabled ? "/restore" : ""}`
									const method = isDisabled ? "patch" : "delete"
									const response = await xhr[method](endpoint)

									const message =
										response.data?.message ||
										`${tripSource.name} has been ${isDisabled ? "enabled" : "disabled"} successfully.`

									showSnackbar(message)
									onSuccess?.()
								} catch (e) {
									const error = e as Error
									showSnackbar(
										error.message || "Something went wrong. Please try again."
									)
								} finally {
									setIsProcessing(false)
								}
							}
						},
					})}
					<ConfirmationDialog
						title={isDisabled ? "Enable B2B Source" : "Disable B2B Source"}
						destructiveAction={!isDisabled}
						acceptActionLabel={
							isDisabled ? "Yes, Enable It" : "I understand, Disable It"
						}
					>
						<Stack gap="2">
							{isDisabled ? (
								<Text>Are you sure you want to enable this B2B source? </Text>
							) : (
								<Text>
									Are you sure you want to disable this B2B source? You can
									always enable this source again if required.
								</Text>
							)}
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

function Filters() {
	const { hasPermission } = useCheckPermissions()
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="created_after"
				toName="created_before"
				label="Created Between"
				dateFormat="DD MMM, YYYY"
			/>
			{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
				<>
					<SelectField
						select={SelectTeams}
						name="teams"
						label="Teams"
						multiple
					/>
					<SelectField
						select={SelectTripSourceTags}
						name="tags"
						multiple
						label="Tag"
						creatable={false}
					/>
				</>
			) : null}
			<SelectField
				select={SelectLocations}
				name="locations"
				multiple
				label="Cities/Locations"
				creatable={false}
				addressableType="trip_sources"
			/>
			<DateRangePickerField
				fromName="stats_start"
				toName="stats_end"
				label="Stats Between"
				dateFormat="DD MMM, YYYY"
			/>
			<SelectField
				select={SelectUsers}
				name="created_by"
				multiple
				label="Created By"
				fetchOnMount
			/>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}

const emailValidator = Validator.string().email()

export function SelectTripSources({
	includeContacts,
	includeTeams,
	onCreateSuccess,
	withRecentlyDisabledWithinDays,
	isAgent,
	notSharedWithRateSheetHead,
	...props
}: {
	includeContacts?: boolean
	includeTeams?: boolean
	onCreateSuccess?: (tripSource: ITripSource) => void
	isAgent?: 1 | 0
	notSharedWithRateSheetHead?: number
	withRecentlyDisabledWithinDays?: number
} & Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const isNewNameAnEmail = emailValidator.isValidSync(newName)
	return (
		<>
			<AsyncSelect
				cacheKey={`trip_sources${withRecentlyDisabledWithinDays ? "_include_recently_deleted" : ""}
				${includeContacts ? "_with_contacts" : ""}
				${notSharedWithRateSheetHead ? `_not_shared_with_rate_sheet_head_${notSharedWithRateSheetHead}` : ""}
				${isAgent ? `_is_agent_${isAgent}` : ""}${includeTeams ? "_with_teams" : ""}`}
				multiple
				{...props}
				fetch={(q) =>
					tripSourcesXHR(xhr)
						.getTripSources({
							q,
							include: [
								"address",
								includeContacts ? "contacts" : "",
								includeTeams ? "teams" : "",
								"tags",
							]
								.filter(Boolean)
								.join(","),
							sort: "name",
							with_recently_disabled_within_days:
								withRecentlyDisabledWithinDays || undefined,
							is_agent: isAgent,
							not_shared_with_rate_sheet_head: notSharedWithRateSheetHead,
						})
						.then((resp) => resp.data)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
				}}
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add new B2B Source "{option.name}"</Box>
					) : (
						<Box>
							<Box>{option.name}</Box>
							<Inline gap={"1"} alignItems={"center"} flexWrap={"wrap"}>
								{option.address ? (
									<Text as="span" fontSize="sm" color="muted">
										{option.address.location.name}
									</Text>
								) : null}
								<TagBadges tags={option.tags} />
								{option.deleted_at ? (
									<Badge status="warning">Disabled</Badge>
								) : null}
							</Inline>
						</Box>
					)
				}
			/>
			<AddTripSourceDialog
				open={!!newName}
				initialValues={{
					...(isNewNameAnEmail
						? { contact: { email: newName } }
						: { name: newName }),
					is_agent: isAgent === undefined ? undefined : isAgent ? "1" : "0",
				}}
				minimalAddress
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(tripSource) => {
					onCreateSuccess?.(tripSource)
					setNewName("")
				}}
				hideTypeToggle={isAgent !== undefined}
			/>
		</>
	)
}
